import React, { createContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const funcDefault = () => {
  // This is intentional
};

export const AddToCoreContext = createContext({
  // Stack-based navigation
  screen: null,
  pushScreen: funcDefault,
  replaceScreen: funcDefault,
  popScreen: funcDefault,
  resetScreens: funcDefault,
  canGoBack: false,
  handleClose: funcDefault,

  // Legacy compat - setScreen maps to pushScreen for migrated child components
  setScreen: funcDefault,

  // Flow state
  addToType: null,
  products: [],
  storeId: null,

  // Product selection
  selectedProductDetails: {},
  setSelectedProductDetails: funcDefault,

  // Feedback
  showSuccessMessage: false,
  setShowSuccessMessage: funcDefault,
  showErrorMessage: false,
  setShowErrorMessage: funcDefault,

  // List/Project state
  selectedGroup: null,
  setSelectedGroup: funcDefault,
  selectedListId: null,
  setSelectedListId: funcDefault,
  selectedListName: '',
  setSelectedListName: funcDefault,
  sourceScreen: null,

  // Refetch
  projectRefetch: funcDefault,
  listRefetch: funcDefault,
});

/**
 * Lightweight provider for core flows only.
 * Uses STACK-BASED navigation — pushScreen/popScreen instead of arbitrary setScreen.
 * Does NOT include template-creator state (AI, search, copy, abandon warning, etc.)
 */
export const AddToCoreProvider = ({ children, value }) => {
  const [screenStack, setScreenStack] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedListName, setSelectedListName] = useState('');
  const [sourceScreen, setSourceScreen] = useState(null);

  const screen = screenStack.length > 0 ? screenStack[screenStack.length - 1] : null;
  const canGoBack = screenStack.length > 1;

  const pushScreen = useCallback((newScreen) => {
    if (!newScreen) return;
    setScreenStack((prev) => {
      if (prev.length > 0 && prev[prev.length - 1] === newScreen) return prev;
      return [...prev, newScreen];
    });
  }, []);

  const replaceScreen = useCallback((newScreen) => {
    if (!newScreen) return;
    setScreenStack((prev) => {
      if (prev.length === 0) return [newScreen];
      if (prev[prev.length - 1] === newScreen) return prev;
      return [...prev.slice(0, -1), newScreen];
    });
  }, []);

  const popScreen = useCallback(() => {
    setScreenStack((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  }, []);

  const resetScreens = useCallback(() => {
    setScreenStack([]);
  }, []);

  // setScreen = pushScreen (compat layer for migrated child components that call setScreen)
  const setScreen = pushScreen;

  const handleClose = useCallback(() => {
    resetScreens();
    if (value.onClose) {
      value.onClose();
    }
  }, [value.onClose, resetScreens]);

  useEffect(() => {
    return () => {
      setSelectedListId(null);
      setSelectedListName('');
    };
  }, []);

  return (
    <AddToCoreContext.Provider value={{
      ...value,
      screen,
      screenStack,
      pushScreen,
      replaceScreen,
      popScreen,
      resetScreens,
      canGoBack,
      setScreen,
      handleClose,
      selectedListId,
      setSelectedListId,
      selectedListName,
      setSelectedListName,
      sourceScreen,
      setSourceScreen,
    }}
    >
      {children}
    </AddToCoreContext.Provider>
  );
};

AddToCoreProvider.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.shape({
    addToType: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.shape({})),
    storeId: PropTypes.string,
    onClose: PropTypes.func,
    selectedProductDetails: PropTypes.shape({}),
    setSelectedProductDetails: PropTypes.func,
    showSuccessMessage: PropTypes.bool,
    setShowSuccessMessage: PropTypes.func,
    showErrorMessage: PropTypes.bool,
    setShowErrorMessage: PropTypes.func,
    selectedGroup: PropTypes.shape({}),
    setSelectedGroup: PropTypes.func,
    projectRefetch: PropTypes.func,
    listRefetch: PropTypes.func,
  })
};

AddToCoreProvider.defaultProps = {
  value: {}
};
